import {
    ListItem,
    ListItemText,
    Divider,
    Menu,
    ListItemIcon,
    MenuItem,
    Popover,
    IconButton,
} from '@mui/material';
import type { MenuProps } from '@mui/material/Menu';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import cx from 'classnames';
import { useDispatch } from 'react-redux';
import { useNotification } from 'src/context/NotificationContext';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import NotificationsIcon from '@mui/icons-material/Notifications';
import styled from 'styled-components';
import AvatarIcon from '../../../assets/images/Icon_Avatar.svg';
import DropdownIcon from '../../../assets/images/icon-dropdown.svg';
import Icon_changePassword from '../../../assets/images/icon-changePassword.svg';
import Help_Icon from '../../../assets/images/help_icon.svg';
import Contact_Support from '../../../assets/images/contact_support.svg';
import Exit_Icon from '../../../assets/images/exit_icon.svg';
import Icon_Logout from '../../../assets/images/icon_Logout.svg';
import useUser from '../../../hooks/useUser';
import useLogout from '../../../hooks/useLogout';
import useFeatures from '../../../hooks/useFeatures';
import SettingIcon from '../Icons/Setting';
import { showGlobalSetting } from '../../../store/setting/actions';
import regionConfig from '../../../config';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FEATURE_FLAG } from 'src/config/common.constants';

const navMenuOptions = [
    { label: 'Home', value: 'home', path: '/home' },
    { label: 'Videomail', value: 'video-mail', path: '/video-mail' },
    { label: 'Call Log', value: 'call-log', path: '/usage' },
    { label: 'Usage Report', vslue: 'b2b-usage', path: '/b2b-usage' },
];

const StyledMenuItem = styled(MenuItem)`
    height: 44px;
    width: 308px;
    position: relative;
    cursor: pointer;
    padding: 0 10px;
`;

const StyledListItemIcon = styled(ListItemIcon)`
    height: 18px;
    width: 18px;
    min-width: 56px !important ;
`;

const StyledListItemText = styled(ListItemText)``;

const menuId = 'primary-search-account-menu';

const InnerStyledMenu = styled(Menu)`
    & .MuiPaper-root {
        width: 308px;
        border-radius: 10px;
        box-shadow: 0px 0px 1px 0px rgba(0, 0, 0, 0.75);
    }
`;

const StyledMenu = (props: MenuProps) => (
    <InnerStyledMenu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
);

const NavMenuRoot = styled.div`
    min-height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;

    @media (min-width: 600px) {
        justify-content: flex-end;
    }

    .navMenuBar {
        flex-direction: row;
        display: flex;
    }

    .navMenuItem {
        text-align: center;
        margin: 0 1.25rem;
        white-space: nowrap;
        cursor: pointer;
        padding: 0.25rem 0;
        border-bottom: 4px solid transparent;
    }

    .active {
        color: #fff;
        border-bottom: 4px solid #00934a;
    }

    .profileName {
        white-space: nowrap;
        margin-right: 0.5rem;
    }

    .profileImage {
        width: 30px;
        margin-right: 0.5rem;
    }

    .arrowDown {
        width: 20px;

        & img {
            width: 20px;
        }
    }
`;

const NotificationIconButton = styled(IconButton)`
    .notification-icon {
        width: 32px;
        height: 32px;
        color: rgba(255, 255, 255, 0.6);
    }
`;

const NavMenu = () => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user = useUser();
    const { directory } = useFeatures();
    const { permission } = useNotification();
    const logout = useLogout();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const isMenuOpen = Boolean(anchorEl);
    const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);

    const { isEnabled } = useFeatureFlag(FEATURE_FLAG.CUSTOMER_SUPPORT_ENABLED);

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleChange = (path: string) => {
        navigate(path);
    };

    const handleSettingClick = () => {
        dispatch(showGlobalSetting());
    };

    const handleLogoutClick = () => {
        handleMenuClose();
        logout();
    };

    const isActiveMenu = (path: string) => {
        return pathname.indexOf(path) > -1;
    };

    const handleNotificationClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setNotificationAnchorEl(event.currentTarget);
    };

    const handleNotificationPopoverClose = () => {
        setNotificationAnchorEl(null);
    };

    const navMenuList = navMenuOptions.filter((option) =>
        option.value === 'directory' ? directory : true,
    );

    const handleHelpClick = () => {
        const URL = isEnabled === true ? regionConfig.newhelpIconUrl : regionConfig.oldhelpIconUrl;
        window.open(URL, '_blank');
    };

    const handleContactSupportClick = () => {
        const URL = regionConfig.contactSupportUrl;
        window.open(URL, '_blank');
    };

    return (
        <NavMenuRoot id="customer-dashboard-navlist-container">
            <nav
                id="customer-dashboard-navlist"
                aria-label="customer-dashboard"
                className="navMenuBar"
            >
                {navMenuList.map(({ label, value, path }) => (
                    <ListItem
                        id={`customer-dashboard-${value}-listitem`}
                        key={path}
                        style={{ cursor: 'pointer' }}
                        className={cx('navMenuItem', {
                            active: isActiveMenu(path),
                        })}
                        onClick={() => handleChange(path)}
                        aria-label={label}
                    >
                        <ListItemText
                            id={`customer-dashboard-${value}-listitemtext`}
                            primary={label}
                        />
                    </ListItem>
                ))}
                {permission === 'granted' && (
                    <NotificationIconButton
                        className="text-white mx-2"
                        aria-label="notification bell"
                    >
                        <NotificationsIcon className="notification-icon" />
                    </NotificationIconButton>
                )}
                {permission === 'denied' && (
                    <NotificationIconButton
                        className="text-white mx-2"
                        onClick={handleNotificationClick}
                        aria-label="notification bell"
                    >
                        <NotificationsOffIcon className="notification-icon" />
                    </NotificationIconButton>
                )}
                <ListItem id="customer-dashboard-settings-listitem" className="navMenuItem">
                    {user && (
                        <span className="profileName">{`${user.firstName} ${user.lastName}`}</span>
                    )}
                    <span className="profileImage">
                        <img alt="Profile avatar" src={AvatarIcon} />
                    </span>
                    <span onClick={handleProfileMenuOpen} className="arrowDown">
                        <img
                            alt="customer"
                            id="customer-dashboard-settings-listitemtext"
                            src={DropdownIcon}
                            role="button"
                            aria-label="open dropdown menu button"
                        />
                    </span>
                </ListItem>
            </nav>
            <StyledMenu
                id={menuId}
                anchorEl={anchorEl}
                keepMounted
                open={isMenuOpen}
                onClose={handleMenuClose}
            >
                <StyledMenuItem
                    id="customer-dashboard-profile-menuitem"
                    onClick={() => {
                        handleMenuClose();
                        navigate('/profile');
                    }}
                    aria-label="Profile"
                >
                    <StyledListItemIcon>
                        <img alt="" style={{ width: '24px' }} src={AvatarIcon} />
                    </StyledListItemIcon>
                    <StyledListItemText primary="Profile" />
                </StyledMenuItem>
                <StyledMenuItem
                    id="customer-dashboard-change-password-menuitem"
                    onClick={() => {
                        handleMenuClose();
                        handleSettingClick();
                    }}
                    aria-label="Settings"
                >
                    <StyledListItemIcon>
                        <SettingIcon />
                    </StyledListItemIcon>
                    <StyledListItemText primary="Settings" />
                </StyledMenuItem>
                <StyledMenuItem
                    id="customer-dashboard-change-password-menuitem"
                    onClick={() => {
                        handleMenuClose();
                        navigate('/changepasswordsetting');
                    }}
                    aria-label="Change Password"
                >
                    <StyledListItemIcon>
                        <img
                            alt=""
                            style={{ marginLeft: '5px', width: '22px', height: '22px' }}
                            src={Icon_changePassword}
                        />
                    </StyledListItemIcon>
                    <StyledListItemText primary="Change Password" />
                </StyledMenuItem>
                <Divider sx={{ '&&': { marginTop: 0, marginBottom: 0 } }} />
                <StyledMenuItem
                    id="customer-dashboard-help-lik-menuitem"
                    onClick={() => {
                        handleHelpClick();
                    }}
                    aria-label="Help Centre"
                >
                    <StyledListItemIcon>
                        <img
                            alt=""
                            style={{
                                width: '22px',
                                height: '22px',
                                marginTop: '-2px',
                                marginLeft: '2.5px',
                            }}
                            src={Help_Icon}
                        />
                    </StyledListItemIcon>
                    <StyledListItemText primary={regionConfig.helpCenterLabel} />
                    <span
                        onClick={() => {
                            handleHelpClick();
                        }}
                    >
                        <StyledListItemIcon>
                            <img
                                alt=""
                                style={{ marginLeft: '22px', height: '22px', marginTop: '6px' }}
                                src={Exit_Icon}
                            />
                        </StyledListItemIcon>
                    </span>
                </StyledMenuItem>
                {isEnabled === true && (
                    <StyledMenuItem
                        id="customer-dashboard-contact-support-menuitem"
                        onClick={handleContactSupportClick}
                        aria-label="Contact Support"
                    >
                        <StyledListItemIcon>
                            <img
                                alt=""
                                style={{ width: '22px', height: '22px', marginLeft: '4px' }}
                                src={Contact_Support}
                            />
                        </StyledListItemIcon>
                        <StyledListItemText primary="Contact Support" />
                        <span
                            onClick={() => {
                                handleContactSupportClick();
                            }}
                        >
                            <StyledListItemIcon>
                                <img
                                    alt=""
                                    style={{
                                        marginLeft: '22px',
                                        height: '22px',
                                        marginTop: '6px',
                                    }}
                                    src={Exit_Icon}
                                />
                            </StyledListItemIcon>
                        </span>
                    </StyledMenuItem>
                )}
                <Divider sx={{ '&&': { marginTop: 0, marginBottom: 0 } }} />
                <StyledMenuItem
                    id="customer-dashboard-logout-menuitem"
                    onClick={handleLogoutClick}
                    aria-label="Logout"
                >
                    <StyledListItemIcon>
                        <img
                            alt=""
                            style={{ width: '18px', height: '18px', marginLeft: '6px' }}
                            src={Icon_Logout}
                        />
                    </StyledListItemIcon>
                    <StyledListItemText primary="Logout" />
                </StyledMenuItem>
            </StyledMenu>
            <Popover
                anchorEl={notificationAnchorEl}
                open={Boolean(notificationAnchorEl)}
                onClose={handleNotificationPopoverClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                PaperProps={{ style: { marginTop: '1rem', width: 300 } }}
            >
                <p className="text-lg p-4">
                    Call notifications are blocked. To allow call notifications, please update your
                    browser settings.
                </p>
            </Popover>
        </NavMenuRoot>
    );
};

export default NavMenu;
